/** A blob reference as it appears in payloads. The device nests it (`image: {blob, mime, bytes}`,
 *  `nodes: {blob,…}`, an image block's `source.data`); older/fake emitters put `blob` at top level. */
export type BlobRef = { sha: string; mime?: string; bytes?: number };
export const blobRef = (v: unknown): BlobRef | null => {
  if (!v) return null;
  if (typeof v === "string") return { sha: v };
  const o = v as any;
  if (typeof o.blob === "string") return { sha: o.blob, mime: o.mime, bytes: o.bytes };
  return null;
};
/** The screenshot ref of a `screen.shot` payload, whichever shape it uses. */
export const shotRef = (p: any): BlobRef | null => blobRef(p?.image) ?? (typeof p?.blob === "string" ? { sha: p.blob, mime: p.mime, bytes: p.bytes } : null);

export const fmtTime = (ts?: number | null) => (ts ? new Date(ts).toLocaleTimeString([], { hour12: false }) : "—");
export const fmtDateTime = (ts?: number | null) => (ts ? new Date(ts).toLocaleString([], { hour12: false }) : "—");
export const fmtMs = (ms?: number | null) => (ms == null ? "—" : ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`);
export const fmtAgo = (ts?: number | null) => {
  if (!ts) return "never";
  const d = Date.now() - ts;
  if (d < 5_000) return "just now";
  if (d < 60_000) return `${Math.round(d / 1000)}s ago`;
  if (d < 3_600_000) return `${Math.round(d / 60_000)}m ago`;
  if (d < 86_400_000) return `${Math.round(d / 3_600_000)}h ago`;
  return `${Math.round(d / 86_400_000)}d ago`;
};
export const fmtNum = (n?: number | null) => (n == null ? "—" : n.toLocaleString());
export const fmtCost = (c?: number | null) => (c == null ? "n/a" : `$${c.toFixed(4)}`);
export const fmtBytes = (b?: number | null) => (b == null ? "—" : b < 1024 ? `${b}B` : b < 1048576 ? `${(b / 1024).toFixed(1)}KB` : `${(b / 1048576).toFixed(1)}MB`);
export const shortFp = (fp?: string | null) => (fp ? fp.slice(0, 8) : "?");
export const shortAct = (a?: string | null) => (a ? a.split(".").pop() ?? a : "?");
export const tail = (s?: string | null) => (s ? s.split("/").pop() ?? s : "");
export const trunc = (s: unknown, n = 140) => {
  const t = typeof s === "string" ? s : JSON.stringify(s) ?? "";
  return t.length > n ? t.slice(0, n - 1) + "…" : t;
};

/** One-line summary for an event row. */
export function summarize(kind: string, p: any): string {
  if (!p || typeof p !== "object") return trunc(p);
  switch (kind) {
    case "run.start":
      return `${p.mode ?? ""} ${JSON.stringify(p.prompt ?? "")}${p.source ? ` (${p.source})` : ""}`;
    case "run.end":
      return `${p.ok ? "ok" : "FAIL"} ${p.result ?? p.error ?? ""}${p.durationMs ? ` · ${(p.durationMs / 1000).toFixed(1)}s` : ""}`;
    case "run.step":
      return String(p.text ?? "");
    case "model.query":
      return `${p.purpose ?? "?"} · ${p.model ?? ""} · ${p.usage ? `${p.usage.input ?? 0}→${p.usage.output ?? 0} tok` : ""} · ${p.latencyMs != null ? `${p.latencyMs}ms` : ""}${p.ok === false ? ` · ERROR ${p.error ?? ""}` : ""}`;
    case "screen.observe":
      return `${shortAct(p.activity)} fp=${shortFp(p.fp)} · ${p.summary?.visibleNodeCount ?? p.sampleIds?.length ?? "?"} visible · ${p.summary?.dumpMs != null ? `${p.summary.dumpMs}ms` : ""}`;
    case "screen.shot":
      return `${p.reason ?? ""} ${p.w ?? "?"}×${p.h ?? "?"} fp=${shortFp(p.fp)} ${shotRef(p)?.bytes ? `(${Math.round(shotRef(p)!.bytes! / 1024)}KB)` : ""}`;
    case "graph.state":
      return `${shortAct(p.activity)} fp=${shortFp(p.fp)} visits=${p.visits ?? ""}`;
    case "graph.edge":
      return `${shortFp(p.fromFp)} --${p.action}--> ${shortFp(p.toFp)}${p.ok === false ? " (failed)" : ""}`;
    case "graph.edge.remove":
      return `removed ${shortFp(p.fromFp)} --${p.action}--> ${shortFp(p.toFp)}`;
    case "loop.tick":
    case "harness.tick":
      return `loop#${p.loopId ?? p.id} ${p.label ?? p.loop ?? ""} · ${(p.actions ?? []).length} actions${p.ok === false ? " · FAIL" : ""}`;
    case "feed.sample":
    case "harness.sample":
      return `${p.label}: ${p.value == null ? "null" : trunc(p.value, 60)}`;
    case "loop.install":
      return `#${p.id ?? p.loopId} ${p.label ?? ""}`;
    case "loop.kill":
      return `#${p.id ?? p.target}`;
    case "capability.learned":
      return `${p.key ?? ""} (${p.kind ?? ""})`;
    case "capability.heal":
    case "capability.quarantine":
      return `${p.key ?? p.loop ?? ""} ${p.step ?? ""}`;
    case "device.hello":
      return `${p.nickname ?? ""} ${p.device?.model ?? ""} sdk${p.device?.sdk ?? ""}`;
    default:
      return trunc(p);
  }
}
